import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { Button } from '../../components/Button'
import { Logo } from '../../components/Logo'
import { Text } from '../../components/Text'
import { ROUTES } from '../../config/constants'
import { useI18n } from '../../i18n'

/**
 * Next-step reminder dialog for the home screen — for users who walked away
 * mid-application and need re-orienting when they come back. Mirrors the
 * status→step mapping used by sampleApplicationStatusFor: the applicant's step
 * is the NDA (→ /nda), the participant's is the KYC application (→ /apply).
 *
 * `NextStepDialog` is the presentational modal (also mounted in the dev
 * gallery); `NextStepReminder` wraps it with the open/close + navigation wiring.
 * The reminder deliberately re-shows on every home-screen mount (see below) so
 * users who keep walking away keep getting nudged.
 */
export type NextStepDialogStep = 'nda' | 'kyc'

const STEP_ROUTE: Record<NextStepDialogStep, string> = {
  nda: ROUTES.nda,
  kyc: ROUTES.apply,
}

const STEP_BODY_KEY = {
  nda: 'nextStep.dialog.nda.body',
  kyc: 'nextStep.dialog.kyc.body',
} as const

export function NextStepDialog({
  step,
  onClose,
  onNext,
}: {
  step: NextStepDialogStep
  onClose: () => void
  onNext: () => void
}) {
  const { t } = useI18n()
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    panelRef.current?.focus()

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  return (
    // Above the fixed headers/tab bar (z-50); backdrop click closes.
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('nextStep.dialog.title')}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        className="w-full max-w-sm rounded-lg bg-white p-6 shadow-[0_10px_30px_rgba(0,0,0,0.2)] focus-visible:outline-none"
      >
        <div className="mb-4 flex justify-center border-b border-gray-200 pb-4">
          <Logo width={72} height={27} />
        </div>

        <Text as="h2" variant="h3" weight="bold" className="mb-2 block text-gray-900">
          {t('nextStep.dialog.title')}
        </Text>
        <Text as="p" variant="base" className="mb-6 block text-gray-700">
          {t(STEP_BODY_KEY[step])}
        </Text>

        <div className="flex gap-3">
          <Button intent="outline" className="flex-1" onClick={onClose}>
            {t('nextStep.dialog.close')}
          </Button>
          <Button intent="filled" className="flex-1" onClick={onNext}>
            {t('nextStep.dialog.next')}
          </Button>
        </div>
      </div>
    </div>
  )
}

export function NextStepReminder({ step }: { step: NextStepDialogStep }) {
  const navigate = useNavigate()
  const [open, setOpen] = useState(true)

  if (!open) return null

  // Dismissal is intentionally NOT persisted: the reminder re-shows every time
  // the home screen mounts (after login, and on returning from the NDA/KYC
  // screen) so a user who keeps walking away keeps getting nudged. Closing only
  // hides it for the current visit.
  return (
    <NextStepDialog
      step={step}
      onClose={() => setOpen(false)}
      onNext={() => {
        setOpen(false)
        navigate(STEP_ROUTE[step])
      }}
    />
  )
}
